/**
 * Moving a departing person's open work to someone else.
 *
 * Deactivating does not touch assignments on purpose — the work is still real
 * and still has to be done by somebody. This is the step that decides who, and
 * it is offered separately so that "they have left" and "here is who picks it
 * up" stay two decisions rather than one silent one.
 */

import { useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errorMessage';
import { useUsers, type PersonView } from '../api';

export function ReassignWorkDialog({
  person,
  open,
  onOpenChange,
}: {
  person: PersonView;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}) {
  const qc = useQueryClient();
  const { data: people } = useUsers({ status: 'active' });
  const [toUserId, setToUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reassign = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ reassigned: number }>(`/users/${person._id}/reassign-work`, { toUserId })
      ).data,
    onSuccess: (data) => {
      toast.success(
        data.reassigned === 0
          ? `${person.name} had no open tasks to move`
          : `Moved ${data.reassigned} open task${data.reassigned === 1 ? '' : 's'}`,
      );
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      onOpenChange(false);
    },
    onError: (err: unknown) => setError(describeError(err, 'The work could not be moved')),
  });

  const candidates = (people ?? []).filter((p) => p._id !== person._id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="text-[16px] font-semibold">
          Move {person.name}’s open work
        </DialogTitle>
        <p className="mb-4 mt-0.5 text-[12px] vt-muted">
          Everything still to do or in progress moves across. Finished work keeps their name on it.
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reassign-to">Move it to</Label>
            <select
              id="reassign-to"
              data-testid="reassign-to"
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
            >
              <option value="">Choose someone…</option>
              {candidates.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
            >
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              data-testid="confirm-reassign"
              onClick={() => reassign.mutate()}
              disabled={!toUserId || reassign.isPending}
            >
              {reassign.isPending ? <Loader2 className="animate-spin" /> : <Users />}
              Move the work
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
